import { useMemo } from 'react';
import { DataGridPro, DataGridProProps, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { MAX_PAGE_SIZE } from '@cbridge-utils/datagrid';
import { ColumnHeaderFilterIcon } from '../_components/ColumnHeaderFilterIcon/ColumnHeaderFilterIcon';
import { BooleanCellFalseChip } from '../_renderers/BooleanCellFalseChip';
import { BooleanCellTrueChip } from '../_renderers/BooleanCellTrueChip';
import { DetailPanelExpandIcon } from '../_renderers/DetailPanelExpandIcon';
import { DetailPanelCollapseIcon } from '../_renderers/DetailPanelCollapseIcon';
import { useGridSettings } from '../_hooks/useGridSettings/useGridSettings';
import { useColumnsSettings } from '../_hooks/useColumnsSettings/useColumnsSettings';
import { composeZebraRowClassName } from '../_helpers/composeZebraRowClassName';
import { IDefaultDataGridProps } from './DefaultDataGrid.interface';

export const DefaultDataGrid = <T extends GridValidRowModel>(props: IDefaultDataGridProps<T>) => {
  const {
    slots,
    columns,
    getRowClassName,
    initialState: {
      sorting,
      columns: initialColumnsState,
      ...initialState
    } = {},
    settingsKey,
    apiRef: externalApiRef,
    columnVisibilityModel,
    onColumnOrderChange,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    ...rest
  } = props;

  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;

  const settings = useGridSettings({ sorting: sorting?.sortModel, columns });
  const {
    orderColumns, columnVisibilityModel: savedVisibilityModel, saveColumnsState, handleColumnWidthChange,
  } = useColumnsSettings(apiRef, settingsKey);

  const orderedColumns = useMemo(() => orderColumns(columns), [columns, orderColumns]);

  // A controlled visibility model belongs to the consumer; the saved one only seeds the uncontrolled grid state.
  const initialColumns = columnVisibilityModel === undefined && savedVisibilityModel
    ? { ...initialColumnsState, columnVisibilityModel: { ...initialColumnsState?.columnVisibilityModel, ...savedVisibilityModel } }
    : initialColumnsState;

  const handleColumnOrderChange: DataGridProProps<T>['onColumnOrderChange'] = (params, event, details) => {
    saveColumnsState();
    onColumnOrderChange?.(params, event, details);
  };

  const handleColumnVisibilityModelChange: DataGridProProps<T>['onColumnVisibilityModelChange'] = (model, details) => {
    saveColumnsState(model);
    onColumnVisibilityModelChange?.(model, details);
  };

  const handleWidthChange: DataGridProProps<T>['onColumnWidthChange'] = (params, event, details) => {
    handleColumnWidthChange(params);
    onColumnWidthChange?.(params, event, details);
  };

  return (
    <DataGridPro<T>
      apiRef={apiRef}
      initialState={{
        ...initialState,
        ...(initialColumns && { columns: initialColumns }),
        ...settings.initialState,
      }}
      columns={orderedColumns}
      columnVisibilityModel={columnVisibilityModel}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      onColumnWidthChange={handleWidthChange}
      onSortModelChange={settings.onSortModelChange}
      pageSizeOptions={[MAX_PAGE_SIZE]}
      hideFooter={true}
      scrollbarSize={0}
      disableColumnMenu={true}
      keepNonExistentRowsSelected={true}
      getRowClassName={composeZebraRowClassName<T>(getRowClassName)}
      slots={{
        booleanCellTrueIcon: BooleanCellTrueChip,
        booleanCellFalseIcon: BooleanCellFalseChip,
        columnHeaderFilterIconButton: ColumnHeaderFilterIcon,
        detailPanelExpandIcon: DetailPanelExpandIcon,
        detailPanelCollapseIcon: DetailPanelCollapseIcon,
        ...slots,
      }}
      {...rest}
    />
  );
};
